import { useMutation, useQueryClient } from '@tanstack/react-query';
import { terraProxyApiClient } from '../../clients/testerClient';
import { SURFACE_ASSETS_KEY } from './useSurfaceAssets';

export function useMergeCanonicalInternalNetworkAsset({
  networkId,
}: {
  networkId: string | null;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sourceAssetId,
      targetAssetId,
      reason,
    }: {
      sourceAssetId: string;
      targetAssetId: string;
      reason: string;
    }) => {
      if (!networkId) {
        throw new Error('Surface ID is required');
      }

      return terraProxyApiClient.mergeCanonicalInternalNetworkAssets({
        networkId,
        sourceAssetId,
        targetAssetId,
        reason,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [SURFACE_ASSETS_KEY, 'canonical-internal'],
      });
      void queryClient.invalidateQueries({
        queryKey: [SURFACE_ASSETS_KEY, 'canonical-internal-detail'],
      });
      void queryClient.invalidateQueries({
        queryKey: [SURFACE_ASSETS_KEY, 'canonical-internal-relationships'],
      });
    },
  });
}

export function useReverseCanonicalInternalNetworkAssetMerge({
  networkId,
}: {
  networkId: string | null;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      mergeEventId,
      reason,
    }: {
      mergeEventId: string;
      reason: string;
    }) => {
      if (!networkId) {
        throw new Error('Surface ID is required');
      }

      return terraProxyApiClient.reverseCanonicalInternalNetworkAssetMerge({
        networkId,
        mergeEventId,
        reason,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [SURFACE_ASSETS_KEY, 'canonical-internal'],
      });
      void queryClient.invalidateQueries({
        queryKey: [SURFACE_ASSETS_KEY, 'canonical-internal-detail'],
      });
      void queryClient.invalidateQueries({
        queryKey: [SURFACE_ASSETS_KEY, 'canonical-internal-relationships'],
      });
    },
  });
}

function invalidateCanonicalAssetQueries(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  void queryClient.invalidateQueries({
    queryKey: [SURFACE_ASSETS_KEY, 'canonical-internal'],
  });
  void queryClient.invalidateQueries({
    queryKey: [SURFACE_ASSETS_KEY, 'canonical-internal-detail'],
  });
  void queryClient.invalidateQueries({
    queryKey: [SURFACE_ASSETS_KEY, 'canonical-internal-relationships'],
  });
}

export function useDismissCanonicalInternalNetworkResolutionIssue({
  networkId,
}: {
  networkId: string | null;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ issueId, reason }: { issueId: string; reason: string }) => {
      if (!networkId) {
        throw new Error('Surface ID is required');
      }

      return terraProxyApiClient.dismissCanonicalInternalNetworkResolutionIssue(
        {
          networkId,
          issueId,
          reason,
        },
      );
    },
    onSuccess: () => {
      invalidateCanonicalAssetQueries(queryClient);
    },
  });
}

export function useKeepCanonicalInternalNetworkAssetsSeparate({
  networkId,
}: {
  networkId: string | null;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ issueId, reason }: { issueId: string; reason: string }) => {
      if (!networkId) {
        throw new Error('Surface ID is required');
      }

      return terraProxyApiClient.keepCanonicalInternalNetworkAssetsSeparate({
        networkId,
        issueId,
        reason,
      });
    },
    onSuccess: () => {
      invalidateCanonicalAssetQueries(queryClient);
    },
  });
}
